import { Alert, Stack } from '@mui/material'
import React, { useMemo, useState } from 'react'

import { MonitorSearchField } from 'components/status-checks/form/status-check/common/MonitorSearchField'
import { MonitorSearchList } from 'components/status-checks/form/status-check/common/MonitorSearchList'
import { shouldRetryPredicateFn } from 'components/status-checks/form/status-check/prometheus/prometheus-monitor-utils'
import { type PrometheusRule } from 'queries/prometheus/types'
import { useSearchPrometheusMonitors } from 'queries/prometheus/use-search-prometheus-monitors'

export const PrometheusMonitorSearch: React.FC<{
  initialValue?: string
  integrationName: string
  isDisabled?: boolean
  selectedRowId: string | undefined
  onSelect(rule: PrometheusRule, groupName: string): void
}> = ({
  initialValue,
  integrationName,
  isDisabled,
  selectedRowId,
  onSelect
}) => {
  const [searchValue, setSearchValue] = useState(initialValue ?? '')

  const {
    data: prometheusRules,
    error,
    isError,
    isFetching,
    isIdle,
    refetch
  } = useSearchPrometheusMonitors(integrationName, searchValue, {
    enabled: Boolean(searchValue.trim()),
    retry: shouldRetryPredicateFn
  })

  const errorMessage = useMemo(() => {
    try {
      return isError
        ? decodeURIComponent(error.message) ??
            'Prometheus monitor search failed'
        : undefined
    } catch (error) {
      return 'Failed to decode the error message received from the integration URL.'
    }
  }, [error?.message, isError])

  const rulesMappedById = useMemo(() => {
    if (!prometheusRules) return

    const map = new Map()

    for (const group of prometheusRules.groups) {
      for (const rule of group.rules) {
        map.set(rule.query, { rule, groupName: group.name })
      }
    }

    return map
  }, [prometheusRules])

  /*
   * We ultimately are selecting a Prometheus Rule, such that we can extract its labels to construct
   * the monitorUrl. However, PrometheusRules are grouped by "groups", so we need to flatten the list
   * to display in the MonitorSearchList.
   */
  const dataGridRows = useMemo(
    () =>
      prometheusRules?.groups.flatMap(({ name: groupName, rules }) =>
        // We have to transform the Rules object to match the `MonitorListGridRow` interface
        rules.map(({ labels, name, query }) => ({
          title: name,
          uid: query,
          labels: [
            /*
             * We want to include the group name as a tag/label so that the information is not lost
             * as we flatten the rules list.
             */
            `group:${groupName}`,
            ...Object.entries(labels).map(([key, value]) => `${key}:${value}`)
          ]
        }))
      ),
    [prometheusRules?.groups]
  )

  /*
   * Ultimately, we need to extract the raw labels from the Rule, which we search through the query
   * results for a matching Rule query field. The labels are then converted into URLSearchParams and
   * appended to the integration's base URL to form the monitorUrl.
   */
  const handleSelectRule = (ruleQuery: string) => {
    const { groupName, rule } = rulesMappedById?.get(ruleQuery) ?? {}

    if (!rule) return

    onSelect(rule, groupName)
  }

  const handleSubmitSearch = (value: string) => {
    // Allows additional searches in the event the initial query has errored out.
    if (isError && value.trim() === searchValue.trim()) {
      refetch()
      return
    }

    setSearchValue(value)
  }

  return (
    <Stack spacing={2}>
      <MonitorSearchField
        disabled={isDisabled}
        helperText="This will filter the first 100 results based on Alert name or tags."
        isLoading={isFetching}
        onSubmit={handleSubmitSearch}
      />
      {isError ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : (
        !isIdle && (
          <MonitorSearchList
            isLoading={isFetching}
            pageSize={5}
            rows={dataGridRows ?? []}
            selectedRowId={selectedRowId}
            onClick={handleSelectRule}
          />
        )
      )}
    </Stack>
  )
}
